import {
  Component,
  ElementRef,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';

import { Session } from '@services/session';
import { FeatureUpgradeService } from './upgrade.service';

import { pushUpsellViewGTMEvent, UPSELL_COMPONENT_EVENT_NAME } from '@lib/gtm-events';

import {
  IAddUnit,
  IAddUnits,
  IGeneralUpsellParams,
  ADD_DEVICES_UPSELL_CONFIG,
 } from '@misc/upsell/upsell-config';

const ERROR_HIDE_TIME = 30000;

@Component({
  selector: 'devices-upsell',
  templateUrl: './devices-upsell.component.html',
  styleUrls: ['./devices-upsell.component.scss', '../../styles/upgrade.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesUpgradeComponent {
  @ViewChild('thankYouModal', { static: true }) public thankYouModal;
  @Input() public featureKey: string;
  @Input() public getQuoteproductInterest: string;
  public options: IAddUnits;
  public units: IAddUnit[];
  public showError: boolean = false;
  public isAMAssigned: boolean;
  public isGetQuoteDisabled: boolean = true;
  public displayHardwareUnits: boolean = true;
  public subscriptionPlan: string;
  public resetProductsDetails: string = '';
  public pardotProductsDetailsField: string;
  public resetQuantity: number = 0;
  public accountManager: any;
  public readonly devicesUpsellId: string = 'add-devices';

  private el: HTMLElement;

  constructor(
    elRef: ElementRef,
    private session: Session,
    private featureUpgradeService: FeatureUpgradeService,
    private cdr: ChangeDetectorRef,
  ) {
    this.el = elRef.nativeElement;
    this.pardotProductsDetailsField = this.resetProductsDetails;
    this.accountManager = session.getAccountManager();
    this.isAMAssigned = !!this.accountManager;
    this.subscriptionPlan = session.getCompanyProperty('subscription_plan');
  }

  public get getQuoteRequested(): boolean {
    return this.options.isGetQuoteBtn ? !this.session.isGetAQuoteRequested : false;
  }

  public get showQuoteRequestedBtn(): boolean {
    return this.options.isGetQuoteRequestedBtn ? this.session.isGetAQuoteRequested : false;
  }

  public ngOnInit(): void {
    this.options = ADD_DEVICES_UPSELL_CONFIG[this.featureKey];
    this.units = this.options.units;
  }

  public ngAfterViewInit(): void {
    if (this.el.offsetParent !== null) {
      // element is visible
      pushUpsellViewGTMEvent(
        UPSELL_COMPONENT_EVENT_NAME['devices-upgrade'],
        this.devicesUpsellId,
      );
    }
  }

  public upgradeToPro(campaignType: string, productInterest: string): void {
    // send empty product interest for HW upsell
    productInterest = (productInterest === 'Add More Units') ? null : productInterest;
    const generalUpsellParams: IGeneralUpsellParams = {
      subscribeTo: campaignType,
      userEmail: this.session.getUserEmail(),
      firstName: this.session.getUserFirstName(),
      lastName: this.session.getUserLastName(),
      companyName: this.session.getCompany().name,
      productInterest,
      comments: this.pardotProductsDetailsField,
    };

    this.featureUpgradeService.generalUpgrade(generalUpsellParams)
      .subscribe((): void => {
        this.thankYouModal.show();
        this.resetModal();
      },
      () => {
        this.handleError();
      });
  }

  public updateUnitQuantity(updatedUnit: IAddUnit, unit: IAddUnit): void {
    if (unit.type === updatedUnit.type) {
      unit.quantity = updatedUnit.quantity;
    }
  }

  public updateUnitPardotFeild(unit: IAddUnit): void {
    if (unit.pardotField !== null) {
      this.pardotProductsDetailsField += `${unit.pardotField} | `;
    }
  }

  public onUnitUpdated(updatedUnit: IAddUnit): void {
    this.resetPardotProductsDetailsField();

    this.units.forEach(unit => {
      this.updateUnitQuantity(updatedUnit, unit);
      this.updateUnitPardotFeild(unit);
    });
    this.isGetQuoteDisabled = !this.hasSomeUnits();
    this.cdr.detectChanges();
  }

  public hasSomeUnits(): boolean {
    return this.units.some(unit => unit.quantity > 0);
  }

  public resetModal(): void {
    this.resetUnitQuantity();
    this.resetPardotProductsDetailsField();
    this.cdr.detectChanges();
  }

  public resetUnitQuantity(): void {
    // returning new object to reflect changes
    this.units = this.units.map((unit: IAddUnit) => {
      return {
        ...unit,
        pardotField: null,
        quantity: this.resetQuantity,
      };
    });
  }

  public resetPardotProductsDetailsField(): void {
    this.pardotProductsDetailsField = this.resetProductsDetails;
  }

  private handleError(): void {
    this.showError = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.showError = false;
      this.cdr.detectChanges();
    }, ERROR_HIDE_TIME);
  }
}
